import { useId, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { BsArrowRepeat, BsCloudArrowUp, BsTrash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import { useErrorStore } from '#lib/stores/errorStore';
import { uploadPostMailingImage } from '#postMailingFeature/api/postMailingMediaApi';

const DEFAULT_ACCEPT = 'image/jpeg,image/png,image/webp';
const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;

// Client-side pre-check mirrors the backend (jpeg/png/webp, ≤ 5 MB) so obvious
// rejects fail instantly without a round-trip. The backend remains the source of truth.
const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

/**
 * Validate a picked file against type + size. Returns an error key string or null.
 * @param {File} file
 * @param {number} maxBytes
 * @returns {string|null}
 */
function validateFile(file, maxBytes) {
	if (!ACCEPTED_TYPES.has(file.type)) return 'type';
	if (file.size > maxBytes) return 'size';
	return null;
}

/**
 * Drag-and-drop / click image upload field. Uploads to object storage via the
 * post-mailing media endpoint and reports the resulting public URL through onChange.
 * Shows a thumbnail of the current value, a busy state while uploading, and a
 * remove button. Errors surface as a toast via the global error store.
 */
export default function ImageUploadField({
	value,
	onChange,
	uploadFn = uploadPostMailingImage,
	accept = DEFAULT_ACCEPT,
	maxBytes = DEFAULT_MAX_BYTES,
	label,
	hint,
	previewShape = 'rect',
	onUploadStateChange,
	disabled = false,
}) {
	const showError = useErrorStore((s) => s.showError);
	const inputRef = useRef(null);
	const inputId = useId();
	const [busy, setBusy] = useState(false);
	const [dragOver, setDragOver] = useState(false);

	const setUploading = (next) => {
		setBusy(next);
		onUploadStateChange?.(next);
	};

	const handleFile = async (file) => {
		if (!file || disabled) return;
		const problem = validateFile(file, maxBytes);
		if (problem === 'type') {
			showError({
				error: { code: 'POSTMAILING_MEDIA_INVALID_TYPE' },
				title: 'Bild konnte nicht hochgeladen werden',
			});
			return;
		}
		if (problem === 'size') {
			showError({
				error: { code: 'POSTMAILING_MEDIA_TOO_LARGE' },
				title: 'Bild konnte nicht hochgeladen werden',
			});
			return;
		}
		setUploading(true);
		try {
			const { url } = await uploadFn(file);
			onChange(url);
		} catch (err) {
			showError({ error: err, title: 'Bild konnte nicht hochgeladen werden' });
		} finally {
			setUploading(false);
		}
	};

	const onInputChange = (e) => {
		const file = e.target.files?.[0];
		handleFile(file);
		// Reset so picking the same file again re-triggers change.
		e.target.value = '';
	};

	const onDrop = (e) => {
		e.preventDefault();
		setDragOver(false);
		if (disabled || busy) return;
		handleFile(e.dataTransfer.files?.[0]);
	};

	const openPicker = () => {
		if (!disabled && !busy) inputRef.current?.click();
	};

	const hasImage = typeof value === 'string' && value.trim() !== '' && !value.includes('{{');
	const previewRounded = previewShape === 'circle' ? 'rounded-full' : 'rounded-md';

	return (
		<div>
			{label && <span className="block text-sm font-medium text-text mb-1.5">{label}</span>}

			<input
				ref={inputRef}
				id={inputId}
				type="file"
				accept={accept}
				className="sr-only"
				onChange={onInputChange}
				disabled={disabled || busy}
			/>

			{hasImage ? (
				<div className="flex items-center gap-3">
					<div
						className={`flex items-center justify-center overflow-hidden border border-gray-200 bg-gray-50 ${previewRounded}`}
						style={{ width: 64, height: 64 }}
					>
						<img src={value} alt="Vorschau" className="w-full h-full object-cover" />
					</div>
					<div className="flex gap-2">
						<Button onClick={openPicker} disabled={disabled || busy} variant="outline" size="sm">
							<BsArrowRepeat className={busy ? 'animate-spin' : ''} /> {busy ? 'Lädt …' : 'Ersetzen'}
						</Button>
						<Button onClick={() => onChange('')} disabled={disabled || busy} variant="outline" size="sm">
							<BsTrash /> Entfernen
						</Button>
					</div>
				</div>
			) : (
				<button
					type="button"
					onClick={openPicker}
					onDragOver={(e) => {
						e.preventDefault();
						if (!disabled && !busy) setDragOver(true);
					}}
					onDragLeave={() => setDragOver(false)}
					onDrop={onDrop}
					disabled={disabled || busy}
					className={`w-full flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-md border-2 border-dashed transition-colors ${
						dragOver
							? 'border-primary-400 bg-primary-100'
							: 'border-gray-300 bg-gray-50 hover:border-gray-400'
					} disabled:opacity-60`}
				>
					{busy ? (
						<BsArrowRepeat className="animate-spin text-lg text-bgBase-800" />
					) : (
						<BsCloudArrowUp className="text-lg text-bgBase-800" />
					)}
					<span className="text-sm text-text">
						{busy ? 'Wird hochgeladen …' : 'Bild hierher ziehen oder klicken'}
					</span>
					<span className="text-xs text-bgBase-800">JPG, PNG oder WebP · max. 5 MB</span>
				</button>
			)}

			{hint && <p className="text-xs text-bgBase-800 mt-1.5">{hint}</p>}
		</div>
	);
}

ImageUploadField.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	uploadFn: PropTypes.func,
	accept: PropTypes.string,
	maxBytes: PropTypes.number,
	label: PropTypes.string,
	hint: PropTypes.string,
	previewShape: PropTypes.oneOf(['rect', 'circle']),
	onUploadStateChange: PropTypes.func,
	disabled: PropTypes.bool,
};
